import React, { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";
import { Warning } from "../../assets/iconComponents/Warning";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { GetTypographyContent } from "../shared/Typography/Typography";
import {
  DetailedResultsFetalSexModalWrapper,
  ActionAndCancelButtonPair,
  ModalTextButton,
} from "./DetailedResults.styles";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { AnalyticsEventNames } from "../../utils/analyticsEvents";
import { getUserAuth } from "../../recoil/selectors";
import { FetalSexModalState } from "./DetailedResults.types";

interface DetailedResultsFetalSexModalProps {
  showModal: (show: FetalSexModalState) => void;
  showFetalSexModal: FetalSexModalState;
  /** Called when user confirms download (for "report" or "report_es" modes). Parent provides the appropriate action. */
  onConfirmDownload: () => Promise<void>;
  showFetalSexAction: () => void;
  setShowFetalSex?: (show: boolean) => void;
}

export const DetailedResultsFetalSexModal = React.forwardRef<
  HTMLDivElement,
  DetailedResultsFetalSexModalProps
>(
  (
    {
      showModal,
      setShowFetalSex,
      showFetalSexModal,
      onConfirmDownload,
      showFetalSexAction,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { deviceType } = useDeviceHook();
    const [downloading, setDownloading] = useState(false);
    const auth = useRecoilValue(getUserAuth);
    // This modal backs both the reveal confirmation and the report-download
    // confirmation. Attribute each to its own analytics name so download
    // confirmations are not counted as fetal-sex reveals.
    const isReportDownload =
      showFetalSexModal === "report" || showFetalSexModal === "report_es";
    const analyticsModalName = isReportDownload
      ? "fetal_sex_report_download_confirmation"
      : "reveal_fetal_sex_confirmation";
    const analyticsConfirmElementName = isReportDownload
      ? "confirm_fetal_sex_report_download"
      : "confirm_reveal_fetal_sex";
    const analyticsCancelElementName = isReportDownload
      ? "cancel_fetal_sex_report_download"
      : "cancel_reveal_fetal_sex";

    useEffect(() => {
      if (auth?.access_token) {
        TrackAnalyticsEvent(
          AnalyticsEventNames.impression,
          auth?.access_token,
          auth?.user_id,
          {
            impression_type: "modal_view",
            modal_name: analyticsModalName,
            page_name: "AneuploidyReportPage",
          },
        );
      }
    }, []);

    const continueFunction = async () => {
      if (auth?.access_token) {
        TrackAnalyticsEvent(
          AnalyticsEventNames.interaction,
          auth?.access_token,
          auth?.user_id,
          {
            interaction_type: "button_click",
            button_text: "Continue",
            element_name: analyticsConfirmElementName,
            modal_name: analyticsModalName,
            page_name: "AneuploidyReportPage",
          },
        );
      }
      if (showFetalSexModal === "reveal" && setShowFetalSex) {
        setShowFetalSex(true);
        showFetalSexAction();
        showModal("");
      } else if (!downloading) {
        setDownloading(true);
        try {
          await onConfirmDownload();
        } catch (error) {
          console.error("Failed to download report:", error);
        } finally {
          setDownloading(false);
          showModal("");
        }
      }
    };

    const getModalText = (): React.ReactNode => {
      if (showFetalSexModal === "report" || showFetalSexModal === "report_es") {
        return (
          <>
            {t("fetalSexModal.body.reportContainsSex.part1")}
            <strong>{t("fetalSexModal.body.reportContainsSex.part2")}</strong>
            {t("fetalSexModal.body.reportContainsSex.part3")}
          </>
        );
      }
      return t("fetalSexModal.body.revealSex");
    };

    const getModalTitle = () => {
      if (showFetalSexModal === "report" || showFetalSexModal === "report_es") {
        return t("fetalSexModal.titles.downloadReport");
      }
      return t("fetalSexModal.titles.revealFetalSex");
    };

    return (
      <DetailedResultsFetalSexModalWrapper
        ref={ref}
        tabIndex={-1}
        deviceType={deviceType}
      >
        <div>
          <Warning />
        </div>
        <div data-testid="reveal-sex-modal-heading">
          {GetTypographyContent({
            content: getModalTitle(),
            size: "h3",
            textColor: "redDark",
            isBold: true,
          })}
        </div>
        <div>
          {GetTypographyContent({
            content: getModalText(),
            size: "large",
            textColor: "greyMedium",
          })}
        </div>
        <ActionAndCancelButtonPair>
          <ModalTextButton
            data-testid="reveal-sex-continue-button"
            onClick={() => continueFunction()}
          >
            {t("fetalSexModal.buttons.continue")}
          </ModalTextButton>
          <ModalTextButton
            data-testid="reveal-sex-cancel-button"
            onClick={() => {
              showModal("");
              if (auth?.access_token) {
                TrackAnalyticsEvent(
                  AnalyticsEventNames.interaction,
                  auth?.access_token,
                  auth?.user_id,
                  {
                    interaction_type: "button_click",
                    button_text: "Cancel",
                    element_name: analyticsCancelElementName,
                    modal_name: analyticsModalName,
                    page_name: "AneuploidyReportPage",
                  },
                );
              }
            }}
          >
            {t("fetalSexModal.buttons.cancel")}
          </ModalTextButton>
        </ActionAndCancelButtonPair>
      </DetailedResultsFetalSexModalWrapper>
    );
  },
);
